/**
 * Diaphragm organ texture — polished scissor jack.
 *
 * The diaphragm is a mechanical-prerequisite organ: it has no molecule
 * logic, just a single castellated spout at the top that signals "I'm
 * here, lungs can function." Its visual is a stacked scissor-lift jack
 * (yellow arms, silver bolts at every pivot, steel base + top plates,
 * with a small coupling fitting at top-center where the castellated
 * joint meets from above).
 *
 * 3 cells wide × 2 cells tall = 150 × 100 px.
 */
import Phaser from 'phaser';
import { GRID_SIZE } from '../../grid';
import type { HasTexture } from './core';

export function registerDiaphragmTextures(scene: Phaser.Scene, has: HasTexture): void {
  if (!has('organ-diaphragm-idle')) makeDiaphragm(scene, 'organ-diaphragm-idle');
  // 'active' variant: same texture for v5.3, animated via scale tween in
  // Organ.activate (existing pulse animation suffices).
  if (!has('organ-diaphragm-active')) makeDiaphragm(scene, 'organ-diaphragm-active');
}

function makeDiaphragm(scene: Phaser.Scene, key: string): void {
  const cols = 3;
  const rows = 2;
  const w = cols * GRID_SIZE; // 150
  const h = rows * GRID_SIZE; // 100
  const g = scene.add.graphics();

  // ----- Palette -----
  const armBody = 0xc4a430; // rich gold-yellow
  const armOutline = 0x342408;
  const armHighlight = 0xf2dc70;
  const armShadow = 0x6a5010;
  const plateBody = 0x5e5e62;
  const plateHighlight = 0x9c9ca0;
  const plateShadow = 0x2a2a2e;
  const plateOutline = 0x161618;
  const fittingBody = 0xa8a8a8;
  const fittingHighlight = 0xe0e0e0;
  const fittingOutline = 0x2a2a2a;

  // ----- Layout -----
  const fittingW = 38;
  const fittingH = 14;
  const fittingX = w / 2 - fittingW / 2;
  const fittingY = 0;

  const topPlateY = fittingH;
  const topPlateH = 8;
  const bottomPlateH = 12;
  const bottomPlateY = h - bottomPlateH;

  const latticeXL = 18;
  const latticeXR = w - 18;
  const latticeYTop = topPlateY + topPlateH + 3;
  const latticeYBot = bottomPlateY - 3;
  const latticeH = latticeYBot - latticeYTop;
  const stackCount = 3;
  const stackH = latticeH / stackCount;

  // ===== 1. Drop shadow under the whole rig =====
  g.fillStyle(0x000000, 0.22);
  g.fillEllipse(w / 2, h - 2, w - 14, 10);

  // ===== 2. Top fitting (where the castellated joint meets from above) =====
  g.fillStyle(fittingBody, 1);
  g.fillRect(fittingX, fittingY, fittingW, fittingH);
  g.fillStyle(fittingHighlight, 0.85);
  g.fillRect(fittingX + 2, fittingY + 2, fittingW - 4, 2);
  g.lineStyle(1.5, fittingOutline, 1);
  g.strokeRect(fittingX, fittingY, fittingW, fittingH);

  // ===== 3. Top plate (beam under the fitting) =====
  drawPlate(g, 6, topPlateY, w - 12, topPlateH, plateBody, plateHighlight, plateShadow, plateOutline);

  // ===== 4. Bottom plate (base) =====
  drawPlate(
    g,
    4,
    bottomPlateY,
    w - 8,
    bottomPlateH,
    plateBody,
    plateHighlight,
    plateShadow,
    plateOutline,
  );

  // ===== 5. Scissor arms (3 stacks of X's) =====
  for (let i = 0; i < stackCount; i++) {
    const y0 = latticeYTop + i * stackH;
    const y1 = y0 + stackH;
    // \ arm: top-left to bottom-right
    drawArm(g, latticeXL, y0, latticeXR, y1, armBody, armHighlight, armShadow, armOutline);
    // / arm: top-right to bottom-left
    drawArm(g, latticeXR, y0, latticeXL, y1, armBody, armHighlight, armShadow, armOutline);
  }

  // ===== 6. Silver bolts at every pivot =====
  // Center pivots (one per X, where the two arms cross)
  for (let i = 0; i < stackCount; i++) {
    const yMid = latticeYTop + i * stackH + stackH / 2;
    drawBolt(g, w / 2, yMid, 5);
  }
  // Edge pivots: top of stack 0, between each stack pair, bottom of stack 2
  for (let i = 0; i <= stackCount; i++) {
    const y = latticeYTop + i * stackH;
    drawBolt(g, latticeXL, y, 5);
    drawBolt(g, latticeXR, y, 5);
  }

  g.generateTexture(key, w, h);
  g.destroy();
}

/** Steel plate with a bright top edge, dark bottom edge, and ink outline. */
function drawPlate(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  body: number,
  highlight: number,
  shadow: number,
  outline: number,
): void {
  g.fillStyle(body, 1);
  g.fillRect(x, y, w, h);
  g.fillStyle(highlight, 0.9);
  g.fillRect(x + 1, y + 1, w - 2, 2);
  g.fillStyle(shadow, 0.9);
  g.fillRect(x + 1, y + h - 2, w - 2, 1);
  g.lineStyle(1.2, outline, 1);
  g.strokeRect(x, y, w, h);
}

/**
 * Scissor arm — dark-outlined yellow capsule with a single highlight stripe
 * along the upper edge so it reads as a flat metal bar catching light from
 * above. The arm runs from (x1,y1) to (x2,y2).
 */
function drawArm(
  g: Phaser.GameObjects.Graphics,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  body: number,
  highlight: number,
  shadow: number,
  outline: number,
): void {
  const r = 4;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const tx = dx / len;
  const ty = dy / len;
  // Perpendicular; flip so it points "up" (negative y) for the highlight side
  let nx = -dy / len;
  let ny = dx / len;
  if (ny > 0) {
    nx = -nx;
    ny = -ny;
  }

  // Outline (slightly larger capsule)
  g.fillStyle(outline, 1);
  drawCapsule(g, x1, y1, x2, y2, r + 1);
  // Body
  g.fillStyle(body, 1);
  drawCapsule(g, x1, y1, x2, y2, r);
  // Highlight stripe along the upper edge
  const inset = 6;
  const offN = r * 0.55;
  g.lineStyle(1.2, highlight, 0.85);
  g.beginPath();
  g.moveTo(x1 + tx * inset + nx * offN, y1 + ty * inset + ny * offN);
  g.lineTo(x2 - tx * inset + nx * offN, y2 - ty * inset + ny * offN);
  g.strokePath();
  // Shadow stripe along the lower edge
  g.lineStyle(1.2, shadow, 0.55);
  g.beginPath();
  g.moveTo(x1 + tx * inset - nx * offN, y1 + ty * inset - ny * offN);
  g.lineTo(x2 - tx * inset - nx * offN, y2 - ty * inset - ny * offN);
  g.strokePath();
}

/** Filled capsule from (x1,y1) to (x2,y2) with the given radius. */
function drawCapsule(
  g: Phaser.GameObjects.Graphics,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  r: number,
): void {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  g.beginPath();
  g.moveTo(x1 + nx * r, y1 + ny * r);
  g.lineTo(x2 + nx * r, y2 + ny * r);
  g.lineTo(x2 - nx * r, y2 - ny * r);
  g.lineTo(x1 - nx * r, y1 - ny * r);
  g.closePath();
  g.fillPath();
  g.fillCircle(x1, y1, r);
  g.fillCircle(x2, y2, r);
}

/** Silver pivot bolt — round head with cross slot, soft drop shadow, top-left highlight. */
function drawBolt(g: Phaser.GameObjects.Graphics, x: number, y: number, r: number): void {
  // Drop shadow
  g.fillStyle(0x000000, 0.3);
  g.fillCircle(x + 0.5, y + 1, r);
  // Body
  g.fillStyle(0xd8d8d8, 1);
  g.fillCircle(x, y, r);
  // Top-left highlight
  g.fillStyle(0xffffff, 0.65);
  g.fillCircle(x - r * 0.35, y - r * 0.35, r * 0.42);
  // Rim
  g.lineStyle(1, 0x666666, 1);
  g.strokeCircle(x, y, r);
  // Cross slot
  g.lineStyle(1, 0x424242, 1);
  g.beginPath();
  g.moveTo(x - r * 0.55, y);
  g.lineTo(x + r * 0.55, y);
  g.moveTo(x, y - r * 0.55);
  g.lineTo(x, y + r * 0.55);
  g.strokePath();
}
